import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemCode } from '@up2d8/api-client';
import { Button, useToast } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { AppAuthFrame } from './app-login-page';
import { PIN_LENGTH, PinPad } from './pin-pad';

/**
 * De medewerker wijzigt zijn eigen pincode.
 *
 * De huidige pincode staat erbij: een telefoon die open op tafel ligt, mag geen nieuwe
 * pincode kunnen zetten. Wie de zijne niet meer weet, gaat langs HR voor een nieuwe
 * activatiecode — dat is geen omweg maar precies de controle die erbij hoort.
 */
export function AppPinPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const navigate = useNavigate();

  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      const { error: failure } = await api.PUT('/api/v1/auth/pin', {
        body: { currentPin, newPin } as never,
      });
      if (failure) {
        const code = problemCode(failure);
        setCurrentPin('');
        setNewPin('');
        if (code === 'VALIDATION_FAILED') {
          const first = (failure as { errors?: { message: string }[] }).errors?.[0];
          setError(first?.message ?? t('pin.rejected'));
        } else if (code === 'RATE_LIMITED') {
          setError(t('appLogin.locked'));
        } else {
          setError(t('pin.currentWrong'));
        }
        return;
      }
      toast.show({ title: t('pin.changed'), tone: 'success' });
      void navigate({ to: '/app' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppAuthFrame title={t('pin.change')} intro={t('pin.intro')}>
      <div className="flex flex-col gap-section">
        <PinPad value={currentPin} onChange={setCurrentPin} label={t('pin.current')} />
        <PinPad value={newPin} onChange={setNewPin} label={t('pin.new')} />

        {error ? (
          <p role="alert" className="rounded-md bg-danger-tint px-3 py-2 text-content text-danger">
            {error}
          </p>
        ) : null}

        <Button
          className="w-full"
          loading={busy}
          disabled={currentPin.length !== PIN_LENGTH || newPin.length !== PIN_LENGTH}
          onClick={() => void submit()}
        >
          {t('common.save')}
        </Button>
        {/* Wie niets wil wijzigen, kan terug zonder de knop van het toestel (testrapport 28 okt.).
            Naar Ik, want daar staat de link naar dit scherm. */}
        <Link to="/app/profile" className="block text-center text-content">
          {t('common.cancel')}
        </Link>
      </div>
    </AppAuthFrame>
  );
}
